import { useState, useRef, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { InputFieldset } from "../ui/inputFieldset";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../ui/radio-group";
import { SearchableSelectFieldset } from "../ui/searchableSelectFieldset";
import { Alert, AlertDescription } from "../ui/alert";
import { Textarea } from "../ui/textarea";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../ui/input-otp";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  AlertCircle,
  Upload,
  Plus,
  X,
  ShieldCheck,
  RefreshCw,
  Users,
  Edit2,
  Trash2,
  Search,
} from "lucide-react";
import { toast } from "sonner";
import productionBackground from "../assets/FlippedKarbala.svg";
import stagingBackground from "../assets/fhimsWallpaper.jpeg";
import logoImage from "../assets/logo.png";
import {
  Country,
  checkOfficeNameAvailability,
  DocumentApiResponse,
  getCountryCity,
  // getUserDetailsByITS,
  NewOneTourRegistration,
  ResendOtpWithITS,
  sendOtpWithITS,
  startRegistration,
  verifyOtp,
  getPublicUserDetailsByITS,
} from "../services/newRegistrationService";
import useDebounce from "../hooks/useDebounce";
import { getScreenDocuments } from "../services/documentService";
import {
  formatFileSizeFromKB,
  formatOtpTime,
  maskEmail,
  maskMobileNumber,
  removeFullSpecialCharacters,
  // removeSpecialCharacters,
  validatePhoneNumberInput,
} from "../utils/commonutils";

interface TORegistrationPageProps {
  onBack: () => void;
}

interface AreaOfOperation {
  id: string;
  country: string | number;
  city: string;
  otherCity: string;
  address: string;
  contactNumber: string;
}

interface DocumentUpload {
  id: string;
  name: string;
  file: File | null;
  isRequired: boolean;
  country?: string;
  acceptedFormatsList?: string[];
  acceptedFormats?: string;
  maxFileSizeKB?: number | string;
}

interface PartnerDocumentUpload {
  id: string;
  name: string;
  file: File | null;
  isRequired: boolean;
  acceptedFormatsList?: string[];
  acceptedFormats?: string;
  maxFileSizeKB?: number | string;
}

interface Partner {
  id: string;
  isNonItsPartner: boolean;
  itsNumber: string;
  name: string;
  email: string;
  mobile: string;
  isPartner: boolean;
  isOfficeStaff: boolean;
  loginRequired: boolean;
  isAuthorizedGroupLeader: boolean;
  documents: PartnerDocumentUpload[];
}

interface AreaErrors {
  country?: string | number;
  city?: string;
  address?: string;
  contactNumber?: string;
}

interface ItsData {
  mumin_Id: string;
  fullName: string;
  itsNumber: string;
  first_Name: string;
  middle_Name?: string;
  lastName: string;
  surname: string;
  arabic_FullName?: string;
  gender: "M" | "F" | "Male" | "Female" | "Other";
  // dateOfBirth: string;
  dob: string;
  email?: string;
  mobile?: string;
  mobile_No?: string;
}

export const TORegistrationPage = ({ onBack }: TORegistrationPageProps) => {
  const [step, setStep] = useState<"its" | "otp" | "form" | "success">("its");
  const [registrationToken, setRegistrationToken] = useState("");
  const [registrationTokenLoading, setRegistrationTokenLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [errors, setErrors] = useState<AreaErrors>({});
  const [apiErrors, setApiErrors] = useState<Record<string, string[]>>({});

  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [countriesList, setCountriesList] = useState<Country[]>([]);

  // ITS Data (auto-populated, read-only)
  const [itsNumber, setItsNumber] = useState("");
  const [itsData, setItsData] = useState<ItsData | null>(null);
  const [viewOtp, setViewOtp] = useState(false);

  // OTP State
  const [otp, setOtp] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpTimer, setOtpTimer] = useState(0);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isOtpVerified, setIsOtpVerified] = useState(false);

  // Manual Input Fields
  const [whatsappUsername, setWhatsappUsername] = useState("");
  // const [passportNumber, setPassportNumber] = useState("");
  const [mobileNumber, setMobileNumber] = useState("");
  const [email, setEmail] = useState("");
  const [alternateMobile, setAlternateMobile] = useState("");
  const [officeName, setOfficeName] = useState("");
  const [isOfficeNameAvailable, setIsOfficeNameAvailable] = useState<
    boolean | null
  >(null);
  const officeNameValidationRequestRef = useRef(0);
  const debouncedOfficeName = useDebounce(officeName.trim(), 200);
  const [businessType, setBusinessType] = useState("");
  const [officeNumber, setOfficeNumber] = useState("");
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [otherCity] = useState("");
  const [permanentAddress, setPermanentAddress] = useState("");

  const backgroundImage =
    process.env.REACT_APP_ENVIRONMENT === "staging"
      ? stagingBackground
      : productionBackground;

  // ------------------ REGISTRATION TOKEN ------------------ //
  useEffect(() => {
    let isMounted = true;
    const loadRegistrationToken = async () => {
      try {
        setRegistrationTokenLoading(true);
        const token = await startRegistration();
        if (isMounted) {
          setRegistrationToken(token);
        }
      } catch (error: unknown) {
        console.error("Failed to initialize registration:", error);
        if (isMounted) {
          setRegistrationToken("");
          toast.error(
            "Failed to initialize registration. Please refresh the page.",
          );
        }
      } finally {
        if (isMounted) {
          setRegistrationTokenLoading(false);
        }
      }
    };
    loadRegistrationToken();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (!debouncedOfficeName) {
      setIsOfficeNameAvailable(null);
      return;
    }

    let isCurrentRequest = true;
    const requestId = officeNameValidationRequestRef.current;

    const validateOfficeName = async () => {
      try {
        const isAvailable =
          await checkOfficeNameAvailability(debouncedOfficeName);

        if (
          !isCurrentRequest ||
          requestId !== officeNameValidationRequestRef.current
        )
          return;

        setIsOfficeNameAvailable(isAvailable);
        setValidationErrors((previousErrors) => {
          const nextErrors = { ...previousErrors };

          if (isAvailable) {
            if (nextErrors.officeName === "Office name already in use") {
              delete nextErrors.officeName;
            }
          } else {
            nextErrors.officeName = "Office name already in use";
          }

          return nextErrors;
        });
      } catch (officeNameError) {
        console.error("Failed to validate office name", officeNameError);
        if (isCurrentRequest) setIsOfficeNameAvailable(null);
      }
    };

    validateOfficeName();

    return () => {
      isCurrentRequest = false;
    };
  }, [debouncedOfficeName]);

  // Area of Operations
  const [areasOfOperation, setAreasOfOperation] = useState<AreaOfOperation[]>(
    [],
  );
  const [currentArea, setCurrentArea] = useState<AreaOfOperation>({
    id: "",
    country: "",
    city: "",
    otherCity: "",
    address: "",
    contactNumber: "",
  });
  const [isAuthorizedGroupLead, setIsAuthorizedGroupLead] = useState(false);

  // Documents
  const [documents, setDocuments] = useState<DocumentUpload[]>([]);

  // Partners
  const [partners, setPartners] = useState<Partner[]>([]);
  const [isPartnerDialogOpen, setIsPartnerDialogOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [partnerFormData, setPartnerFormData] = useState<Partner>({
    id: "",
    isNonItsPartner: false,
    itsNumber: "",
    name: "",
    email: "",
    mobile: "",
    isPartner: false,
    isOfficeStaff: false,
    loginRequired: false,
    isAuthorizedGroupLeader: false,
    documents: documents.map((doc) => ({
      id: String(doc.id),
      name: doc.name,
      file: null,
      isRequired: doc.isRequired,
      acceptedFormatsList: doc?.acceptedFormatsList,
      acceptedFormats: doc?.acceptedFormats,
      maxFileSizeKB: doc.maxFileSizeKB,
    })),
  });
  const [isFetchingPartnerITS, setIsFetchingPartnerITS] = useState(false);
  const [partnerITSError, setPartnerITSError] = useState("");
  const [partnerEmailError, setPartnerEmailError] = useState("");
  const [partnerFieldErrors, setPartnerFieldErrors] = useState<
    Record<string, string>
  >({});
  const [partnerDocumentsError, setPartnerDocumentsError] = useState("");

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (itsData?.mobile_No) {
      setMobileNumber(itsData.mobile_No);
    }
  }, [itsData]);

  //  FETCH COUNTRY & CITY & DOCUMENT
  const fetchCountryCity = useCallback(async () => {
    try {
      const result = await getCountryCity();
      if (!result) {
        return;
      }
      setCountriesList(result);
    } catch (error) {
      console.error("Failed to load documents");
    }
  }, []);

  const fetchDocuments = useCallback(
    async (screenName: string): Promise<DocumentUpload[]> => {
      const response = await getScreenDocuments(screenName);
      if (!response) return [];

      return response.map((doc: DocumentApiResponse) => ({
        id: String(doc.documentId),
        name: doc.documentName,
        file: null,
        isRequired: doc.isMandatory,
        acceptedFormatsList: doc?.acceptedFormatsList,
        // acceptedFormats: doc?.acceptedFormatsList,
        maxFileSizeKB: doc?.maxFileSizeKB,
      }));
    },
    [],
  );

  useEffect(() => {
    if (!isOtpVerified) return;
    fetchCountryCity();
    fetchDocuments("tour_operator_registration").then((docs) => {
      setDocuments(docs);
    });
  }, [isOtpVerified, fetchCountryCity, fetchDocuments]);
  //  FETCH COUNTRY & CITY


  // SEND OTP WITH ITS
  const handleFetchITS = async () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    setOtp("");
    setOtpTimer(0);

    if (!itsNumber || itsNumber.length !== 8) {
      setError("Please enter a valid 8-digit ITS number");
      return;
    }

    setIsLoading(true);
    setError("");
    try {
      const info = await sendOtpWithITS(itsNumber, registrationToken, true);
      if (!info && info == null) {
        toast.info("No record found for this ITS.");
        setError("Please enter a valid 8-digit ITS number");
        return;
      } else {
        setIsSendingOtp(true);
        setEmail(info?.maskedEmail);
        setViewOtp(true);
        setTimeout(() => {
          setIsSendingOtp(false);
          setOtpTimer(300);
          toast.success(info?.message || "ITS Number verified successfully");
          // Start countdown timer
          timerIntervalRef.current = setInterval(() => {
            setOtpTimer((prev) => {
              if (prev <= 1) {
                if (timerIntervalRef.current) {
                  clearInterval(timerIntervalRef.current);
                  timerIntervalRef.current = null;
                }
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }, 1500);
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "ITS Number not found in the system. Please verify and try again.";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpGoBack = () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }

    setOtp("");
    setOtpTimer(0);
    setViewOtp(false);
    setItsNumber("");
    setItsData(null);
    setMobileNumber("");
    setEmail("");
    setError("");
    setIsSendingOtp(false);
    setIsVerifyingOtp(false);
  };

  // RESEND OTP
  const handleResendOtp = async () => {
    try {
      const info = await ResendOtpWithITS(itsNumber, registrationToken);
      console.log("Resend OTP response:", info);
      if (!info && info == null) {
        toast.info("No record found for this ITS.");
        return;
      } else {
        setIsSendingOtp(true);
        setEmail(info?.maskedEmail);
        setViewOtp(true);
        setOtp("");
        setTimeout(() => {
          setIsSendingOtp(false);
          setOtpTimer(300);
          toast.success(info?.message || "ITS Number verified successfully");
          // Start countdown timer
          timerIntervalRef.current = setInterval(() => {
            setOtpTimer((prev) => {
              if (prev <= 1) {
                if (timerIntervalRef.current) {
                  clearInterval(timerIntervalRef.current);
                  timerIntervalRef.current = null;
                }
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        }, 1500);
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "ITS Number not found in the system. Please verify and try again.";
      toast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpVerification = async () => {
    if (otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }
    try {
      setIsVerifyingOtp(true);
      const response = await verifyOtp({ itsNumber, registrationToken, otp, force: false });
      if (response) {
        setItsData(response);
        setIsOtpVerified(true);
        setStep("form");
      }
    } catch (error: unknown) {
      const err = error as {
        message?: string;
        response?: {
          data?: {
            message?: string;
          };
        };
      };
      const errorMessage =
        err?.response?.data?.message ||
        err?.message ||
        "OTP verification failed";
      setIsSendingOtp(false);
      toast.error(errorMessage);
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required field validations
    // if (!passportNumber.trim()) {
    //   errors.passportNumber = "Passport number is required";
    // } else if (!/^[A-Za-z0-9]+$/.test(passportNumber)) {
    //   errors.passportNo =
    //     "Passport number must contain only letters and numbers";
    // }
    if (!mobileNumber.trim()) errors.mobileNumber = "Mobile number is required";
    if (!officeName.trim()) {
      errors.officeName = "Office name is required";
    } else if (isOfficeNameAvailable === false) {
      errors.officeName = "Office name already in use";
    }
    if (!businessType) errors.businessType = "Business type is required";
    if (!officeNumber.trim()) errors.officeNumber = "Office number is required";
    if (!country) errors.country = "Country is required";
    if (!city) errors.city = "City is required";
    if (!permanentAddress.trim())
      errors.permanentAddress = "Permanent address is required";

    // Validate mobile number format (basic)
    // const phoneRegex = /^[\d\s\-\+\(\)]+$/;
    const phoneRegex = /^[\d\s\-+()]+$/;
    if (mobileNumber && !phoneRegex.test(mobileNumber)) {
      errors.mobileNumber = "Please enter a valid mobile number";
    }

    // Validate partners for non-sole proprietor businesses
    if (
      businessType &&
      businessType !== "sole_proprietor" &&
      partners.length === 0
    ) {
      errors.partners = "Please add at least one member for this business type";
    }

    // Validate area of operations
    if (areasOfOperation.length === 0) {
      errors.areaOfOperation = "Please add at least one area of operation";
    }

    // Validate required documents
    const missingDocs = documents.filter((doc) => doc.isRequired && !doc.file);
    if (missingDocs.length > 0) {
      errors.documents = `Please upload all required documents`;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddArea = () => {
    const newErrors: AreaErrors = {};

    if (!currentArea.country) {
      newErrors.country = "Please select a country for the area of operation";
    }

    if (!currentArea.city) {
      newErrors.city = "Please select a city for the area of operation";
    }

    if (!currentArea.address.trim()) {
      newErrors.address = "Please enter an address for the area of operation";
    }

    if (!currentArea.contactNumber.trim()) {
      newErrors.contactNumber =
        "Please enter a contact number for the area of operation";
    } else if (
      currentArea.contactNumber.replace(/\D/g, "").length < 10 ||
      currentArea.contactNumber.replace(/\D/g, "").length > 15
    ) {
      newErrors.contactNumber =
        "Contact number must be between 10 and 15 digits";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const newArea = {
      ...currentArea,
      id: Date.now().toString(),
    };

    setAreasOfOperation((prev) => [...prev, newArea]);
    setCurrentArea({
      id: "",
      country: "",
      city: "",
      otherCity: "",
      address: "",
      contactNumber: "",
    });
    setErrors({});
  };

  const handleRemoveArea = (id: string) => {
    setAreasOfOperation(areasOfOperation.filter((area) => area.id !== id));
  };

  const handleFileUpload = (docId: string, file: File | null) => {
    setDocuments(
      documents.map((doc) => (doc.id === docId ? { ...doc, file } : doc)),
    );
  };

  // Partner Management Functions
  const handleOpenPartnerDialog = async () => {
    setEditingPartner(null);
    // fetch partner documents
    setPartnerFormData({
      id: "",
      isNonItsPartner: false,
      itsNumber: "",
      name: "",
      email: "",
      mobile: "",
      isPartner: false,
      isOfficeStaff: businessType === "sole_proprietor",
      loginRequired: false,
      isAuthorizedGroupLeader: false,
      // documents: partnerDocs, // ✅ API-driven
      documents: documents.map((doc) => ({
        id: doc.id,
        name: doc.name,
        file: null,
        isRequired: doc.isRequired,
        acceptedFormats: doc?.acceptedFormats,
        acceptedFormatsList: doc?.acceptedFormatsList,
        maxFileSizeKB: doc?.maxFileSizeKB,
      })),
    });
    setPartnerITSError("");
    setPartnerEmailError("");
    setPartnerFieldErrors({});
    setPartnerDocumentsError("");
    setIsFetchingPartnerITS(false);
    setIsPartnerDialogOpen(true);
  };

  const handleEditPartner = (partner: Partner) => {
    setEditingPartner(partner);
    setPartnerFormData(
      businessType === "sole_proprietor"
        ? { ...partner, isPartner: false, isOfficeStaff: true }
        : partner,
    );
    setPartnerITSError("");
    setPartnerEmailError("");
    setPartnerFieldErrors({});
    setPartnerDocumentsError("");
    setIsFetchingPartnerITS(false);
    setIsPartnerDialogOpen(true);
  };

  const validatePartnerDocuments = () => {
    if (partnerFormData.isOfficeStaff) {
      return true;
    }

    const missingRequiredDocs = partnerFormData.documents.filter(
      (doc) => doc.isRequired && !doc.file,
    );

    if (missingRequiredDocs.length > 0) {
      setPartnerDocumentsError("Please upload all required documents");
      return false;
    }

    setPartnerDocumentsError("");
    return true;
  };

  const handleSavePartner = () => {
    const hasSelectedMemberType =
      partnerFormData.isPartner || partnerFormData.isOfficeStaff;
    const trimmedEmail = partnerFormData.email.trim();
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail);
    const nextFieldErrors: Record<string, string> = {};
    const itsError = partnerFormData.itsNumber ? "" : "ITS Number is required";
    const emailError = !trimmedEmail
      ? "Email is required"
      : !isValidEmail
        ? "Please enter a valid email address"
        : "";

    if (!partnerFormData.name.trim()) {
      nextFieldErrors.name = "Full Name is required";
    }
    if (!partnerFormData.mobile.trim()) {
      nextFieldErrors.mobile = "Mobile Number is required";
    }
    if (!hasSelectedMemberType) {
      nextFieldErrors.memberType = "Please select a member type";
    }

    setPartnerITSError(itsError);
    setPartnerEmailError(emailError);
    setPartnerFieldErrors(nextFieldErrors);
    const areDocumentsValid = validatePartnerDocuments();

    if (
      itsError ||
      emailError ||
      Object.keys(nextFieldErrors).length > 0 ||
      !areDocumentsValid
    ) {
      return;
    }

    //DUPLICATE EMAIL CHECK
    const isEmailExists = partners.some(
      (p) =>
        p.email.toLowerCase() === partnerFormData.email.toLowerCase() &&
        p.id !== editingPartner?.id,
    );

    if (isEmailExists) {
      toast.error("This email already exists");
      return;
    }

    if (editingPartner) {
      // Update existing partner
      setPartners(
        partners.map((p) => (p.id === editingPartner.id ? partnerFormData : p)),
      );
      toast.success("Partner updated successfully");
    } else {
      // Add new partner
      const newPartner = { ...partnerFormData, id: Date.now().toString() };
      setPartners([...partners, newPartner]);
      toast.success("Member added successfully");
    }
    setIsPartnerDialogOpen(false);
  };

  const handleRemovePartner = (id: string) => {
    setPartners(partners.filter((p) => p.id !== id));
    toast.success("Partner removed");
  };

  const handlePartnerFileUpload = (docId: string, file: File | null) => {
    setPartnerDocumentsError("");
    setPartnerFormData({
      ...partnerFormData,
      documents: partnerFormData.documents.map((doc) =>
        doc.id === docId ? { ...doc, file } : doc,
      ),
    });
  };

  // RESET PARTNER FORM ITS DETAILS BLANK
  useEffect(() => {
    setPartnerFormData((prev) => ({
      ...prev,
      itsNumber: partnerFormData?.isNonItsPartner ? "99999999" : "",
      name: "",
      email: "",
      mobile: "",
    }));
  }, [partnerFormData.isNonItsPartner]);

  const handleFetchPartnerITS = async (itsNumber: string) => {
    if (itsNumber.length !== 8) {
      setPartnerITSError("Please enter a valid 8-digit ITS number");
      return;
    }
    setIsFetchingPartnerITS(true);
    setPartnerITSError("");
    try {
      // const itsDatas = await getUserDetailsByITS(itsNumber, registrationToken,true);
      const itsDatas = await getPublicUserDetailsByITS(itsNumber, registrationToken,true);
      if (!itsDatas?.mumin_Id && !itsDatas?.muminDataId) {
        toast.info("No record found for this ITS.");
        setPartnerITSError("Please enter valid ITS number");
        setPartnerFormData((prev) => ({
          ...prev,
          itsNumber: "",
          name: "",
          email: "",
          mobile: "",
        }));
        return;
      } else {
        // Auto-populate partner fields from ITS data
        setPartnerFormData({
          ...partnerFormData,
          itsNumber: itsDatas?.mumin_Id || "",
          name: `${itsDatas.first_Name} ${itsDatas.middle_Name} ${itsDatas.surname}`,
          email: itsDatas.email || partnerFormData.email,
          mobile: itsDatas.mobile_No || partnerFormData.mobile,
        });
        toast.success("Partner ITS details fetched successfully");
      }
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : "ITS Number not found in the system. Please verify and try again.";
      setPartnerITSError(errorMessage || "ITS Number not found");
      toast.error(errorMessage || "Failed to fetch ITS details");
    } finally {
      setIsFetchingPartnerITS(false);
    }
  };

  const getApiError = (field: string) => {
    return apiErrors[field] ? apiErrors[field][0] : null;
  };

  const resetForm = () => {
    setStep("its");

    // ITS + OTP
    setOtp("");
    setOtpTimer(0);
    setViewOtp(false);
    setItsNumber("");
    setItsData(null);
    setIsOtpVerified(false);

    // Errors
    setError("");
    setErrors({});
    setApiErrors({});
    setValidationErrors({});

    // Inputs
    setWhatsappUsername("");
    // setPassportNumber("");
    setMobileNumber("");
    setEmail("");
    setAlternateMobile("");
    setOfficeName("");
    setBusinessType("");
    setOfficeNumber("");
    setCountry("");
    setCity("");
    setPermanentAddress("");
    setIsAuthorizedGroupLead(false);

    // Areas
    setAreasOfOperation([]);
    setCurrentArea({
      id: "",
      country: "",
      city: "",
      otherCity: "",
      address: "",
      contactNumber: "",
    });

    // Partners
    setPartners([]);
    setEditingPartner(null);
    setPartnerFormData({
      id: "",
      isNonItsPartner: false,
      itsNumber: "",
      name: "",
      email: "",
      mobile: "",
      isPartner: false,
      isOfficeStaff: false,
      loginRequired: false,
      isAuthorizedGroupLeader: false,
      documents: [],
    });

    // Documents
    setDocuments([]);

    // Timer cleanup
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = null;
    }
  };

  const validateFile = (doc: DocumentUpload, file: File): boolean => {
    /* ------------------ FORMAT VALIDATION ------------------ */
    if (doc.acceptedFormatsList?.length) {
      const fileExtension = file.name.split(".").pop()?.toLowerCase();

      const allowedFormats = doc.acceptedFormatsList.map((f: string) =>
        f.trim().toLowerCase(),
      );

      if (!fileExtension || !allowedFormats.includes(fileExtension)) {
        toast.error(
          `Accepted format: ${allowedFormats.join(", ").toUpperCase()} only`,
        );
        return false;
      }
    }
    /* ------------------ SIZE VALIDATION ------------------ */
    if (doc.maxFileSizeKB) {
      const maxSizeBytes = Number(doc.maxFileSizeKB) * 1024;
      const tolerance = 5 * 1024;

      if (file.size > maxSizeBytes + tolerance) {
        toast.error(
          `File size must be less than or equal to ${formatFileSizeFromKB(
            doc.maxFileSizeKB,
          )}`,
        );
        return false;
      }
    }

    return true;
  };

  // HANDLE SUBMIT FUNCTION
  const handleSubmit = async () => {
    setError("");

    if (!validateForm()) {
      setError("Please fill in all required fields correctly");
      toast.error("Please fill in all required fields");
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    try {
      const isAvailable = await checkOfficeNameAvailability(officeName.trim());
      setIsOfficeNameAvailable(isAvailable);

      if (!isAvailable) {
        setValidationErrors((previousErrors) => ({
          ...previousErrors,
          officeName: "Office name already in use",
        }));
        return;
      }
    } catch (officeNameError) {
      console.error("Failed to validate office name", officeNameError);
      toast.error("Unable to validate office name. Please try again.");
      return;
    }

    setIsLoading(true);

    try {
      const formData = new FormData();
      formData.append("ITSNumber", itsData?.mumin_Id || "");
      // formData.append('email', "touser1@gmail.com");
      formData.append("email", itsData?.email || "");
      formData.append("firstName", itsData?.first_Name || "");
      formData.append("middleName", itsData?.middle_Name || "");
      formData.append("lastName", itsData?.lastName || itsData?.surname || "");
      formData.append("arabicName", itsData?.arabic_FullName || "");
      formData.append(
        "gender",
        itsData?.gender === "M"
          ? "male"
          : itsData?.gender === "F"
            ? "female"
            : "other",
      );
      // formData.append('dateOfBirth', itsData?.dob ? new Date(itsData.dob).toISOString() : "");
      formData.append(
        "dateOfBirth",
        itsData?.dob ? new Date(itsData.dob).toISOString().split("T")[0] : "",
      );
      formData.append("mobileNumber", mobileNumber || "");
      formData.append("alternateMobile", alternateMobile || "");
      formData.append("WhatsAppUsername", whatsappUsername || "");
      // formData.append("passportNumber", passportNumber || "");
      formData.append("officeName", officeName || "");
      formData.append("officeNumber", officeNumber || "");
      formData.append("businessType", businessType || "");
      formData.append(
        "isAuthorizedGroupLead",
        isAuthorizedGroupLead ? "true" : "false",
      );
      formData.append("LoginRequired", "false");
      formData.append("countryId", String(country));
      formData.append("cityId", String(city));
      formData.append("otherCity", otherCity);
      formData.append("permanentAddress", permanentAddress);
      //// ✅ AREAS OF OPERATION
      areasOfOperation.forEach((a, index) => {
        formData.append(
          `AreasOfOperation[${index}].countryId`,
          String(a.country),
        );
        formData.append(
          `AreasOfOperation[${index}].cityId`,
          a.city === "not_listed" ? "0" : String(a.city),
        );
        formData.append(
          `AreasOfOperation[${index}].otherCity`,
          a.city === "not_listed" ? a.otherCity : "",
        );
        formData.append(`AreasOfOperation[${index}].address`, a.address);
        formData.append(
          `AreasOfOperation[${index}].contactNumber`,
          a.contactNumber,
        );
        formData.append(
          `AreasOfOperation[${index}].isAuthorizedGroupLeader`,
          isAuthorizedGroupLead ? "true" : "false",
        );
      });
      //// ✅ PARTNERS
      partners.forEach((p, index) => {
        formData.append(`Partners[${index}].itsNumber`, p.itsNumber);
        formData.append(
          `Partners[${index}].IsNonIts`,
          p.isNonItsPartner ? "true" : "false",
        );
        formData.append(`Partners[${index}].name`, p.name);
        formData.append(`Partners[${index}].email`, p.email);
        formData.append(`Partners[${index}].mobile`, p.mobile);
        formData.append(
          `Partners[${index}].role`,
          p.isPartner ? "PARTNER" : "OFFICE_STAFF",
        );
        formData.append(
          `Partners[${index}].loginRequired`,
          String(p.loginRequired),
        );
        formData.append(
          `Partners[${index}].isAuthorizedGroupLeader`,
          p.isAuthorizedGroupLeader ? "true" : "false",
        );
        formData.append(`Partners[${index}].isMainOperator`, "true");

        // ✅ DOCUMENT UPLOAD FOR PARTNER
        if (!p.isOfficeStaff)
          p.documents?.forEach((doc, dIndex) => {
            if (doc.file) {
              formData.append(
                `Partners[${index}].Documents[${dIndex}].documentMasterId`,
                String(doc.id),
              );
              formData.append(
                `Partners[${index}].Documents[${dIndex}].file`,
                doc.file,
              );
              formData.append(
                `Partners[${index}].Documents[${dIndex}].FileKey`,
                doc.file.name,
              );
            }
          });
      });
      // ✅ MAIN DOCUMENTS UPLOADS
      documents.forEach((doc, index) => {
        if (!doc.file) return;
        // ✔ REQUIRED: actual file
        formData.append(`Documents[${index}].documentMasterId`, String(doc.id));
        formData.append(`Documents[${index}].File`, doc.file);
        formData.append(`Documents[${index}].FileKey`, doc.file.name);
      });
      formData.append("CreatedBy", "1");

      // console.table(
      //   Array.from(formData.entries()).map(([key, value]) => ({
      //     field: key,
      //     value: value instanceof File ? `File: ${value.name}` : value,
      //   })),
      // );

      // api call
      const result = await NewOneTourRegistration(formData);
      if (!result.success) {
        setApiErrors(result.errors);
        toast.error(result.message);
        return;
      }
      setStep("success");
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : "Failed to submit registration. Please try again.";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const selectedAreaCountry = countriesList.find(
    (c) => c.countryId === currentArea.country,
  );

  // SUCCESS SCREEN
  if (step === "success") {
    return (
      <div className="min-h-screen w-full flex items-center justify-center relative overflow-hidden">
        {/* Full Screen Background Image */}
        <div className="absolute inset-0 w-full h-full">
          <img
            src={backgroundImage}
            alt="Mosque Background"
            className="w-full h-full object-cover"
          />
          {/* Overlay for visibility */}
          <div className="absolute inset-0 bg-black/30"></div>
        </div>

        {/* Content */}
        <Card className="relative w-full max-w-2xl shadow-2xl mx-4 bg-white backdrop-blur-sm border-[#5B9BD5]/20">
          <CardContent className="pt-16 pb-12">
            <div className="text-center space-y-6">
              <div className="flex justify-center">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-green-600" />
                </div>
              </div>
              <div className="space-y-2">
                <h2 className="text-gray-900">
                  Registration Submitted Successfully!
                </h2>
                <p className="text-gray-600 max-w-md mx-auto">
                  Your tour operator registration has been submitted for review.
                  An admin will verify your application and you will be notified
                  via email once approved.
                </p>
              </div>
              <div className="pt-4">
                <Button
                  onClick={onBack}
                  className="text-white shadow-lg hover:opacity-90 transition-opacity px-8 rounded-xl"
                  style={{ backgroundColor: "#5B9BD5" }}
                >
                  Return to Login
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ITS VERIFICATION STEP & OTP SCREEN
  if (step === "its") {
    return (
      <div className="min-h-screen w-full relative">
        {/* Fixed Background Image */}
        <div className="fixed inset-0 w-full h-full -z-10">
          <img
            src={backgroundImage}
            alt="Mosque Background"
            className="w-full h-full object-cover"
          />
          {/* Overlay for visibility */}
          <div className="absolute inset-0 bg-black/30"></div>
        </div>

        {/* Scrollable Content Container */}
        <div className="relative min-h-screen flex items-center justify-center p-4 sm:p-6 py-8 sm:py-12">
          <div className="w-full max-w-2xl my-auto">
            {/* Logo Above Card */}
            <div className="flex justify-center mb-3 sm:mb-4">
              <img
                src={logoImage}
                alt="Faiz-e-Husayni Society Logo"
                className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 object-contain drop-shadow-2xl"
              />
            </div>

            {/* Title */}
            <div className="text-center mb-4 sm:mb-6">
              <h1 className="text-white text-lg sm:text-xl md:text-2xl mb-1 drop-shadow-lg">
                FAIZ E HUSAYNI - IRAQ
              </h1>
              <p className="text-white/90 text-sm drop-shadow-lg">
                Tour Operator Registration
              </p>
            </div>

            {/* Main Card */}
            <Card className="shadow-2xl bg-white backdrop-blur-sm border-none rounded-2xl sm:rounded-3xl">
              <CardContent className="p-5 sm:p-6 md:p-8">
                {/* Shield Icon & Title */}
                <div className="text-center mb-5 sm:mb-6">
                  <div className="flex justify-center mb-3 sm:mb-4">
                    <div className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 bg-[#E3F2FD] rounded-full flex items-center justify-center">
                      <ShieldCheck
                        className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10"
                        style={{ color: "#5B9BD5" }}
                      />
                    </div>
                  </div>
                  <h2 className="text-xl sm:text-2xl text-gray-900 mb-2">
                    Verify Your ITS Number
                  </h2>
                  <p className="text-sm sm:text-base text-gray-600 px-2">
                    Enter your 8-digit ITS number to begin the verification
                    process
                  </p>
                </div>

                <div className="space-y-4 sm:space-y-5">
                  {error && (
                    <Alert
                      variant="destructive"
                      className="border-red-200 bg-red-100/50 rounded-xl"
                    >
                      <AlertCircle className="w-4 h-4" />
                      <AlertDescription className="text-red-800">
                        {error}
                      </AlertDescription>
                    </Alert>
                  )}

                  {/* ITS Number Input */}
                  <div className="space-y-1.5">
                    <InputFieldset
                      id="itsNumber"
                      label="ITS Number"
                      required
                      type="text"
                      maxLength={8}
                      value={itsNumber}
                      onChange={(e) =>
                        setItsNumber(e.target.value.replace(/\D/g, ""))
                      }
                      placeholder="12345678"
                      className="bg-white text-gray-900 tracking-wider"
                      disabled={isLoading || itsData !== null}
                    />
                    {isLoading && (
                      <p className="text-sky-600 text-sm mt-1 flex items-center gap-1">
                        <Search className="h-3 w-3 animate-pulse" />
                        Fetching ITS data...
                      </p>
                    )}
                    {/* <p className="text-xs text-gray-500">
                      Example ITS numbers: 12345678, 87654321, 11223344, 44332211
                    </p> */}
                  </div>

                  {!viewOtp && (
                    <Button
                      onClick={handleFetchITS}
                      disabled={
                        registrationTokenLoading ||
                        !registrationToken ||
                        isLoading ||
                        itsNumber.length !== 8
                      }
                      className="w-full h-9 text-white shadow-lg hover:opacity-90 transition-opacity text-sm"
                      style={{ backgroundColor: "#5B9BD5" }}
                    >
                      {registrationTokenLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Initializing Registration...
                        </>
                      ) : isLoading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Verifying ITS Number...
                        </>
                      ) : (
                        <>
                          <ShieldCheck className="w-4 h-4 mr-2" />
                          Verify ITS Number
                        </>
                      )}
                    </Button>
                  )}

                  {/* Back to Login - Shows only when ITS is not verified */}
                  {!viewOtp && (
                    <div className="text-center pt-1">
                      <button
                        type="button"
                        onClick={onBack}
                        className="text-sm text-gray-600 hover:text-gray-800 inline-flex items-center gap-2"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        Back to Login
                      </button>
                    </div>
                  )}

                  {/* OTP Section - Shows after ITS is verified */}
                  {viewOtp && (
                    <>
                      {/* OTP Input */}
                      <div className="space-y-1.5">
                        <Label className="text-sm text-gray-900 block text-center">
                          Enter OTP <span className="text-red-500">*</span>
                        </Label>
                        <div className="flex justify-center">
                          <InputOTP
                            maxLength={6}
                            value={otp}
                            onChange={(value) => setOtp(value)}
                          >
                            <InputOTPGroup className="gap-2">
                              <InputOTPSlot
                                index={0}
                                className="w-10 h-10 sm:w-12 sm:h-12 border-gray-200 bg-white text-gray-900"
                              />
                              <InputOTPSlot
                                index={1}
                                className="w-10 h-10 sm:w-12 sm:h-12 border-gray-200 bg-white text-gray-900"
                              />
                              <InputOTPSlot
                                index={2}
                                className="w-10 h-10 sm:w-12 sm:h-12 border-gray-200 bg-white text-gray-900"
                              />
                              <InputOTPSlot
                                index={3}
                                className="w-10 h-10 sm:w-12 sm:h-12 border-gray-200 bg-white text-gray-900"
                              />
                              <InputOTPSlot
                                index={4}
                                className="w-10 h-10 sm:w-12 sm:h-12 border-gray-200 bg-white text-gray-900"
                              />
                              <InputOTPSlot
                                index={5}
                                className="w-10 h-10 sm:w-12 sm:h-12 border-gray-200 bg-white text-gray-900"
                              />
                            </InputOTPGroup>
                          </InputOTP>
                        </div>
                        <p className="text-xs text-gray-500 text-center">
                          OTP sent to {email}
                        </p>
                      </div>

                      {/* Verify Button */}
                      <Button
                        onClick={handleOtpVerification}
                        disabled={
                          isVerifyingOtp || otp.length !== 6 || otpTimer === 0
                        }
                        className="w-full h-9 text-white shadow-lg hover:opacity-90 transition-opacity text-sm"
                        style={{ backgroundColor: "#5B9BD5" }}
                      >
                        {isVerifyingOtp ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Verifying OTP
                          </>
                        ) : (
                          <>
                            <CheckCircle2 className="w-4 h-4 mr-2" />
                            Verify & Continue
                          </>
                        )}
                      </Button>

                      {/* Resend OTP and Back to Login */}
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-6">
                        <button
                          type="button"
                          onClick={handleOtpGoBack}
                          className="text-sm text-gray-600 hover:text-gray-800 inline-flex items-center gap-2"
                        >
                          <ArrowLeft className="w-4 h-4" />
                          Go Back
                        </button>

                        {otpTimer === 0 && (
                          <button
                            onClick={handleResendOtp}
                            disabled={isSendingOtp}
                            className="text-sm inline-flex items-center gap-2 hover:opacity-80 transition-opacity"
                            style={{ color: "#5B9BD5" }}
                          >
                            {isSendingOtp ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <RefreshCw className="w-4 h-4" />
                                Resend OTP
                              </>
                            )}
                          </button>
                        )} 
                      </div>

                      {otpTimer > 0 && (
                        <p className="text-xs sm:text-sm text-gray-500 text-center">
                          {/* OTP expires in <span className="font-medium" style={{ color: '#5B9BD5' }}>{otpTimer}s</span> */}
                          OTP expires in{" "}
                          <span
                            className={`font-medium ${otpTimer <= 30 ? "text-red-500" : "text-blue-400"}`}
                          >
                            {formatOtpTime(otpTimer)}
                          </span>
                        </p>
                      )}
                    </>
                  )}

                  {/* Footer */}
                  <div className="pt-3 sm:pt-4 border-t border-gray-200">
                    <p className="text-xs text-center text-gray-500 px-2">
                      Your data is retrieved securely through IP-authenticated
                      API from the ITS server. All personal information is
                      encrypted and handled with strict confidentiality.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // REGISTRATION FORM STEP
  return (
    <div className="min-h-screen w-full relative">
      {/* Fixed Background Image */}
      <div className="fixed inset-0 w-full h-full -z-10">
        <img
          src={backgroundImage}
          alt="Mosque Background"
          className="w-full h-full object-cover"
        />
        {/* Overlay for visibility */}
        <div className="absolute inset-0 bg-black/30"></div>
      </div>

      {/* Scrollable Content Container */}
      <div className="relative min-h-screen py-8 sm:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          {/* Logo Above Form */}
          <div className="flex justify-center mb-3 sm:mb-4">
            <img
              src={logoImage}
              alt="Faiz-e-Husayni Society Logo"
              className="w-20 h-20 sm:w-24 sm:h-24 md:w-28 md:h-28 object-contain drop-shadow-2xl"
            />
          </div>

          {/* Title */}
          <div className="text-center mb-6 sm:mb-8">
            <h1 className="text-white text-lg sm:text-xl md:text-2xl mb-1 drop-shadow-lg">
              FAIZ E HUSAYNI - IRAQ
            </h1>
            <p className="text-white/90 text-sm drop-shadow-lg">
              Tour Operator Registration
            </p>
          </div>

          {error && (
            <Alert
              variant="destructive"
              className="mb-6 border-red-200 bg-red-100/50 rounded-xl"
            >
              <AlertCircle className="w-4 h-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-6">
            {/* ITS Verified Information - Read Only */}
            <Card className="border-none bg-white backdrop-blur-sm shadow-2xl rounded-2xl sm:rounded-3xl">
              <CardHeader
                style={{ backgroundColor: "#E3F2FD" }}
                className="pb-3 rounded-t-lg"
              >
                <div className="flex items-center gap-2">
                  <ShieldCheck
                    className="w-5 h-5"
                    style={{ color: "#5B9BD5" }}
                  />
                  <CardTitle className="text-gray-900 text-base">
                    Verified Personal Information
                  </CardTitle>
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  The following information has been retrieved from the ITS
                  server and cannot be edited
                </p>
              </CardHeader>
              <CardContent className="p-4 sm:p-5">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="space-y-1.5">
                    <InputFieldset
                      label="ITS Number"
                      value={itsData?.itsNumber || itsData?.mumin_Id || ""}
                      readOnly
                      className="!bg-gray-100 cursor-not-allowed tracking-wider [&_legend]:!bg-gray-100"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <InputFieldset
                      label="First Name"
                      value={itsData?.first_Name || ""}
                      readOnly
                      className="!bg-gray-100 cursor-not-allowed [&_legend]:!bg-gray-100"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <InputFieldset
                      label="Middle Name"
                      value={itsData?.middle_Name || ""}
                      readOnly
                      className="!bg-gray-100 cursor-not-allowed [&_legend]:!bg-gray-100"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <InputFieldset
                      label="Last Name"
                      value={itsData?.lastName || itsData?.surname || ""}
                      readOnly
                      className="!bg-gray-100 cursor-not-allowed [&_legend]:!bg-gray-100"
                    />
                  </div>

                  <div className="space-y-1.5 lg:col-span-2">
                    <InputFieldset
                      label="Arabic Name"
                      value={itsData?.arabic_FullName || ""}
                      readOnly
                      dir="rtl"
                      className="!bg-gray-100 cursor-not-allowed [&_legend]:!bg-gray-100"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <InputFieldset
                      label="Gender"
                      value={
                        itsData?.gender === "M"
                          ? "Male"
                          : itsData?.gender === "F"
                            ? "Female"
                            : "Other"
                      }
                      readOnly
                      className="!bg-gray-100 cursor-not-allowed [&_legend]:!bg-gray-100"
                    />
                    {getApiError("gender") && (
                      <p className="mt-1 text-xs text-red-600">
                        {getApiError("gender")}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <InputFieldset
                      label="Date of Birth"
                      value={itsData?.dob.split("T")[0] || ""}
                      readOnly
                      className="!bg-gray-100 cursor-not-allowed [&_legend]:!bg-gray-100"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Manual Input Fields */}
            <Card className="border-none bg-white backdrop-blur-sm shadow-2xl rounded-2xl sm:rounded-3xl">
              <CardHeader>
                <CardTitle className="text-gray-900">
                  Additional Information
                </CardTitle>
                <p className="text-sm text-gray-600 mt-1">
                  Please provide the following details to complete your
                  registration
                </p>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="space-y-1.5">
                    <InputFieldset
                      id="whatsappUsername"
                      label="Whatsapp Username"
                      value={whatsappUsername}
                      onChange={(e) => setWhatsappUsername(e.target.value)}
                      placeholder="e.g., john_doe"
                      className={`bg-white ${validationErrors.whatsappUsername ? "border-red-500" : ""}`}
                    />
                    {validationErrors.whatsappUsername && (
                      <p className="text-xs text-red-600">
                        {validationErrors.whatsappUsername}
                      </p>
                    )}
                  </div>

                  {/* <div className="space-y-1.5">
                    <Label
                      htmlFor="passportNumber"
                      className="text-sm text-gray-900"
                    >
                      Passport Number <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="passportNumber"
                      value={passportNumber}
                      onChange={(e) =>
                        setPassportNumber(
                          removeSpecialCharacters(e.target.value),
                        )
                      }
                      placeholder="e.g., A1234567"
                      className={`h-9 bg-white border-gray-200 ${validationErrors.passportNumber ? "border-red-500" : ""}`}
                    />
                    {validationErrors.passportNumber && (
                      <p className="text-xs text-red-600">
                        {validationErrors.passportNumber}
                      </p>
                    )}
                  </div> */}

                  <div className="space-y-1.5">
                    <InputFieldset
                      id="mobileNumber"
                      label="Mobile Number"
                      required
                      value={mobileNumber}
                      onChange={(e) =>
                        setMobileNumber(
                          validatePhoneNumberInput(e.target.value),
                        )
                      }
                      placeholder="Include country code eg. +91XXXXXXXX01"
                      className={`bg-white ${validationErrors.mobileNumber ? "border-red-500" : ""}`}
                    />
                    {validationErrors.mobileNumber && (
                      <p className="text-xs text-red-600">
                        {validationErrors.mobileNumber}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <InputFieldset
                      id="alternateMobile"
                      label="Alternate Mobile Number"
                      value={alternateMobile}
                      onChange={(e) =>
                        setAlternateMobile(
                          validatePhoneNumberInput(e.target.value),
                        )
                      }
                      placeholder="Include country code eg. +91XXXXXXXX01"
                      className="bg-white"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <InputFieldset
                      id="officeNumber"
                      label="Office Contact Number"
                      required
                      value={officeNumber}
                      onChange={(e) =>
                        setOfficeNumber(
                          validatePhoneNumberInput(e.target.value),
                        )
                      }
                      placeholder="Include country code eg. +91XXXXXXXX01"
                      className={`bg-white ${validationErrors.officeNumber ? "border-red-500" : ""}`}
                    />
                    {validationErrors.officeNumber && (
                      <p className="text-xs text-red-600">
                        {validationErrors.officeNumber}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <InputFieldset
                      id="officeName"
                      label="Office Name"
                      required
                      value={officeName}
                      onChange={(e) => {
                        officeNameValidationRequestRef.current += 1;
                        setOfficeName(e.target.value);
                        setIsOfficeNameAvailable(null);
                        setValidationErrors((previousErrors) => {
                          const nextErrors = { ...previousErrors };
                          delete nextErrors.officeName;
                          return nextErrors;
                        });
                      }}
                      placeholder="Enter your travel agency/office name"
                      className={`bg-white ${validationErrors.officeName ? "border-red-500" : ""}`}
                    />
                    {validationErrors.officeName && (
                      <p className="text-xs text-red-600">
                        {validationErrors.officeName}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2 bg-white">
                    <SearchableSelectFieldset
                      label="Business Type"
                      value={businessType}
                      options={[
                        { value: "sole_proprietor", label: "Sole Proprietor" },
                        { value: "partnership", label: "Partnership" },
                        { value: "llp", label: "LLP" },
                        { value: "company", label: "Company" },
                      ]}
                      placeholder="Select business type..."
                      searchPlaceholder="Search business type..."
                      error={validationErrors.businessType}
                      onChange={setBusinessType}
                    />
                  </div>

                  <div className="space-y-1">
                    <SearchableSelectFieldset
                      label="Country"
                      value={country}
                      options={countriesList.map((countryOption) => ({
                        value: String(countryOption.countryId),
                        label: countryOption.countryName,
                      }))}
                      placeholder="Select country..."
                      searchPlaceholder="Search country..."
                      onChange={(value) => {
                        setCountry(value);
                        setValidationErrors((previousErrors) => {
                          const nextErrors = { ...previousErrors };
                          delete nextErrors.country;
                          return nextErrors;
                        });
                      }}
                    />
                    {validationErrors.country && (
                      <p className="text-xs text-red-600">
                        {validationErrors.country}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <InputFieldset
                      id="city"
                      label="City"
                      required
                      value={city}
                      onChange={(e) => {
                        setCity(e.target.value);
                        setValidationErrors((previousErrors) => {
                          const nextErrors = { ...previousErrors };
                          delete nextErrors.city;
                          return nextErrors;
                        });
                      }}
                      placeholder="Enter city"
                      className={`bg-white ${validationErrors.city ? "border-red-500" : ""}`}
                    />
                    {validationErrors.city && (
                      <p className="text-xs text-red-600">
                        {validationErrors.city}
                      </p>
                    )}
                  </div>

                  <div className="space-y-1.5 lg:col-span-4">
                    <Label
                      htmlFor="permanentAddress"
                      className="text-sm text-gray-900"
                    >
                      Permanent Address <span className="text-red-500">*</span>
                    </Label>
                    <Textarea
                      id="permanentAddress"
                      value={permanentAddress}
                      onChange={(e) => setPermanentAddress(e.target.value)}
                      placeholder="Enter your complete permanent address"
                      className={`bg-input-background min-h-[100px] bg-white border-gray-200 resize-none ${validationErrors.permanentAddress ? "border-red-500" : ""}`}
                    />
                    {validationErrors.permanentAddress && (
                      <p className="text-xs text-red-600">
                        {validationErrors.permanentAddress}
                      </p>
                    )}
                  </div>

                  {/* Add Authorized Group Leader checkbox for Sole Proprietor */}
                  {businessType === "sole_proprietor" && (
                    <div className="lg:col-span-4">
                      <div className="flex items-center space-x-3 p-4 border rounded-xl bg-green-50 border-green-200">
                        <Checkbox
                          id="isAGLAdditional"
                          checked={isAuthorizedGroupLead}
                          onCheckedChange={(checked) =>
                            setIsAuthorizedGroupLead(!!checked)
                          }
                        />
                        <Label
                          htmlFor="isAGLAdditional"
                          className="text-sm cursor-pointer font-medium text-gray-900"
                        >
                          Authorized Group Leader
                        </Label>
                      </div>
                      {getApiError("isAuthorizedGroupLead") && (
                        <p className="mt-1 text-xs text-red-600">
                          {getApiError("isAuthorizedGroupLead")}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Partner and office staff management */}
            <Card className="border-none bg-white backdrop-blur-sm shadow-2xl rounded-2xl sm:rounded-3xl">
              <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50">
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-gray-900 flex items-center gap-2">
                      <Users className="w-5 h-5 text-green-600" />
                      Partners & Team Members
                    </CardTitle>
                    <p className="text-sm text-gray-600 mt-1">
                      {businessType === "sole_proprietor"
                        ? "Add office staff for your Sole Proprietorship"
                        : `Add partners and team members for your ${
                            businessType === "partnership"
                              ? "Partnership"
                              : businessType === "llp"
                                ? "LLP"
                                : "Company"
                          }`}
                    </p>
                  </div>
                  <Button
                    type="button"
                    onClick={handleOpenPartnerDialog}
                    className="h-9 px-4 text-white rounded-lg shadow-sm flex items-center gap-2"
                    style={{ backgroundColor: "#10B981" }}
                  >
                    <Plus className="w-4 h-4" />
                    Add Member
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4">
                {validationErrors.partners && (
                  <Alert
                    variant="destructive"
                    className="mb-4 border-red-200 bg-red-50"
                  >
                    <AlertDescription className="text-red-600 text-sm">
                      {validationErrors.partners}
                    </AlertDescription>
                  </Alert>
                )}

                {partners.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>No partners added yet</p>
                    <p className="text-sm mt-1">
                      Click "Add Member" to add team members
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {partners.map((partner, index) => (
                      <div
                        key={partner.id}
                        className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              <h4 className="text-gray-900">
                                Partner #{index + 1}
                              </h4>
                              <div className="flex gap-2 flex-wrap">
                                {partner.isPartner && (
                                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded">
                                    Partner
                                  </span>
                                )}
                                {partner.isOfficeStaff && (
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                                    Office Staff
                                  </span>
                                )}
                                {partner.loginRequired && (
                                  <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded">
                                    Login Required
                                  </span>
                                )}
                                {partner.isAuthorizedGroupLeader && (
                                  <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                                    AGL
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
                              <div>
                                <span className="text-gray-600">
                                  ITS Number:
                                </span>
                                <span className="ml-2 text-gray-900">
                                  {partner.itsNumber}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Name:</span>
                                <span className="ml-2 text-gray-900">
                                  {partner.name}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Email:</span>
                                <span className="ml-2 text-gray-900">
                                  {partner.email}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-600">Mobile:</span>
                                <span className="ml-2 text-gray-900">
                                  {partner.mobile}
                                </span>
                              </div>
                              <div className="col-span-2">
                                <span className="text-gray-600">
                                  Documents Uploaded:
                                </span>
                                <span className="ml-2 text-gray-900">
                                  {
                                    partner.documents.filter((d) => d.file)
                                      .length
                                  }{" "}
                                  /{" "}
                                  {
                                    partner.documents.filter(
                                      (d) => d.isRequired,
                                    ).length
                                  }{" "}
                                  required
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2 ml-4">
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleEditPartner(partner)}
                              className="h-8 w-8 p-0"
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => handleRemovePartner(partner.id)}
                              className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Area of Operations */}
            <Card className="border-none bg-white backdrop-blur-sm shadow-2xl rounded-2xl sm:rounded-3xl">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-foreground">
                      Area of Operations
                    </CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">
                      Add one or more areas where you operate your tour business
                    </p>
                  </div>
                  {/* Show Authorized Group Leader checkbox only for non-sole proprietor */}
                  {businessType && businessType !== "sole_proprietor" && (
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="isAGL"
                        checked={isAuthorizedGroupLead}
                        onCheckedChange={(checked) =>
                          setIsAuthorizedGroupLead(!!checked)
                        }
                      />
                      <label
                        htmlFor="isAGL"
                        className="text-sm text-foreground cursor-pointer"
                      >
                        Authorized Group Leader
                      </label>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {validationErrors.areaOfOperation && (
                  <Alert
                    variant="destructive"
                    className="mb-6 border-destructive/50 bg-destructive/10 border-red-400"
                  >
                    <AlertCircle className="w-4 h-4" />
                    <AlertDescription className="text-destructive text-sm">
                      {validationErrors.areaOfOperation}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Add Area Form */}
                <div className="p-6 bg-white/50 rounded-lg border border-gray-200 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3 gap-4">
                    <div className="space-y-1.5">
                      <SearchableSelectFieldset
                        label="Country"
                        value={String(currentArea.country)}
                        options={countriesList.map((countryOption) => ({
                          value: String(countryOption.countryId),
                          label: countryOption.countryName,
                        }))}
                        placeholder="Select country..."
                        searchPlaceholder="Search country..."
                        onChange={(value) => {
                          const selectedCountry = countriesList.find(
                            (countryOption) =>
                              String(countryOption.countryId) === value,
                          );
                          if (!selectedCountry) return;
                          setCurrentArea({
                            ...currentArea,
                            country: selectedCountry.countryId,
                            city: "",
                            otherCity: "",
                          });
                        }}
                      />
                      {errors.country && (
                        <p className="text-red-400 text-xs">{errors.country}</p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <SearchableSelectFieldset
                        label="City"
                        value={currentArea.city}
                        options={[
                          ...(selectedAreaCountry?.cities.map((cityOption) => ({
                            value: String(cityOption.cityId),
                            label: cityOption.cityName,
                          })) || []),
                          { value: "not_listed", label: "Not Listed" },
                        ]}
                        placeholder="Select city..."
                        searchPlaceholder="Search city..."
                        disabled={!currentArea.country}
                        onChange={(value) => {
                          setCurrentArea({
                            ...currentArea,
                            city: value,
                            ...(value === "not_listed"
                              ? {}
                              : { otherCity: "" }),
                          });
                        }}
                      />
                      {errors.city && (
                        <p className="text-red-400 text-xs">{errors.city}</p>
                      )}
                    </div>

                    {currentArea.city === "not_listed" && (
                      <div className="space-y-1.5">
                        <InputFieldset
                          id="areaOtherCity"
                          label="Other City"
                          value={currentArea.otherCity}
                          onChange={(e) =>
                            setCurrentArea({
                              ...currentArea,
                              otherCity: e.target.value,
                            })
                          }
                          placeholder="If not listed"
                          className="bg-white"
                        />
                      </div>
                    )}

                    <div className="space-y-1.5 md:col-span-2">
                      <InputFieldset
                        id="areaAddress"
                        label="Address"
                        required
                        value={currentArea.address}
                        onChange={(e) =>
                          setCurrentArea({
                            ...currentArea,
                            address: e.target.value,
                          })
                        }
                        placeholder="Enter complete address"
                        className="bg-white"
                      />
                      {errors.address && (
                        <p className="text-red-400 text-xs">{errors.address}</p>
                      )}
                    </div>

                    <div className="space-y-1.5">
                      <InputFieldset
                        // type="number"
                        id="areaContact"
                        label="Contact Number"
                        required
                        value={currentArea.contactNumber}
                        onChange={(e) =>
                          setCurrentArea({
                            ...currentArea,
                            contactNumber: validatePhoneNumberInput(
                              e.target.value,
                            ),
                          })
                        }
                        placeholder="Include country code eg. +91XXXXXXXX01"
                        className="bg-white"
                      />
                      {errors.contactNumber && (
                        <p className="text-red-400 text-xs">
                          {errors.contactNumber}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Button
                      type="button"
                      onClick={handleAddArea}
                      className="text-white shadow-lg hover:opacity-90 transition-opacity rounded-xl"
                      style={{ backgroundColor: "#5B9BD5" }}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Area
                    </Button>
                  </div>
                </div>

                {/* Display added areas */}
                {areasOfOperation.length > 0 && (
                  <div className="space-y-3">
                    <Label className="text-foreground">
                      Added Areas ({areasOfOperation.length})
                    </Label>
                    <div className="space-y-2">
                      {areasOfOperation.map((area, index) => {
                        const areaCountry = countriesList.find(
                          (c) => c.countryId === area.country,
                        );
                        const areaCity = areaCountry?.cities.find(
                          (c) => String(c.cityId) === area.city,
                        );
                        return (
                          <div
                            key={area.id}
                            className="flex items-center justify-between p-4 bg-card rounded-lg border border-border hover:border-primary/30 transition-colors"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span
                                  className="flex items-center justify-center w-6 h-6 rounded-full text-xs"
                                  style={{
                                    backgroundColor: "rgba(91, 155, 213, 0.1)",
                                    color: "#5B9BD5",
                                  }}
                                >
                                  {index + 1}
                                </span>
                                <p className="text-foreground">
                                  {areaCountry?.countryName} -{" "}
                                  {areaCity?.cityName}
                                  {area.otherCity && ` (${area.otherCity})`}
                                </p>
                              </div>
                              <p className="text-sm text-muted-foreground ml-8">
                                {area.address} • {area.contactNumber}
                              </p>
                            </div>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => handleRemoveArea(area.id)}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Upload Documents */}
            <Card className="border-none bg-white backdrop-blur-sm shadow-2xl rounded-2xl sm:rounded-3xl">
              <CardHeader>
                <CardTitle className="text-gray-900">
                  Upload Required Documents{" "}
                  <span className="text-destructive">*</span>
                </CardTitle>
                {/* <p className="text-sm text-muted-foreground mt-1">
                  Please upload all mandatory documents. Accepted formats: PDF, JPEG, GIF (Max 5MB each)
                </p> */}
              </CardHeader>
              <CardContent>
                {validationErrors.documents && (
                  <Alert
                    variant="destructive"
                    className="mb-6 border-destructive/50 bg-destructive/10 border-red-400"
                  >
                    <AlertCircle className="w-4 h-4" />
                    <AlertDescription className="text-destructive text-sm">
                      {validationErrors.documents}
                    </AlertDescription>
                  </Alert>
                )}

                {/* Desktop Table View */}
                <div className="hidden md:block overflow-hidden rounded-lg border border-border">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-card-foreground text-card">
                        <th className="px-4 py-3 text-left w-16">No.</th>
                        <th className="px-4 py-3 text-left">Document Name</th>
                        <th className="px-4 py-3 text-left">Upload File</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {documents.map((doc, index) => (
                        <tr
                          key={doc.id}
                          className="bg-card-table hover:bg-muted/30 transition-colors"
                        >
                          <td className="px-4 py-4">
                            <span className="flex items-center justify-center w-8 h-8 rounded-full bg-muted text-foreground">
                              {index + 1}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <p className="text-foreground">
                              {doc.name}
                              {doc.isRequired && (
                                <span className="text-destructive ml-1">*</span>
                              )}
                            </p>
                            <p className="text-xs text-red-400">
                              {`Accepted format: ${
                                doc.acceptedFormatsList?.join(", ") || "N/A"
                              } (Max ${formatFileSizeFromKB(doc.maxFileSizeKB)})`}
                            </p>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              <input
                                className="hidden"
                                type="file"
                                id={`file-${doc.id}`}
                                // accept=".pdf,.jpeg,.jpg,.gif"
                                // onChange={(e) => handleFileUpload(doc.id, e.target.files?.[0] || null)}
                                accept={
                                  doc.acceptedFormatsList?.length
                                    ? doc.acceptedFormatsList
                                        .map((f: string) => "." + f.trim())
                                        .join(",")
                                    : "*"
                                }
                                onChange={(e) => {
                                  const file = e.target.files?.[0] || null;
                                  if (!file) return;
                                  const isValid = validateFile(doc, file);
                                  if (!isValid) {
                                    e.target.value = ""; // reset input
                                    return;
                                  }
                                  handleFileUpload(doc.id, file);
                                }}
                              />
                              <Button
                                type="button"
                                onClick={() =>
                                  document
                                    .getElementById(`file-${doc.id}`)
                                    ?.click()
                                }
                                variant={doc.file ? "outline" : "default"}
                                className={
                                  doc.file
                                    ? "border-primary text-primary hover:bg-primary/10"
                                    : "bg-primary hover:bg-primary/90 text-primary-foreground"
                                }
                                size="sm"
                              >
                                <Upload className="w-4 h-4 mr-2" />
                                {doc.file ? "Change File" : "Choose File"}
                              </Button>
                              {doc.file && (
                                <div className="flex items-center gap-1 text-xs text-green-600">
                                  <CheckCircle2 className="w-4 h-4" />
                                  <span className="max-w-[200px] truncate">
                                    {doc.file.name}
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Mobile Card View */}
                <div className="md:hidden space-y-4">
                  {documents.map((doc, index) => (
                    <div
                      key={doc.id}
                      className="bg-card border border-border rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      {/* Header with Number */}
                      <div className="flex items-start gap-3 mb-3">
                        <span className="flex items-center justify-center w-10 h-10 rounded-full bg-muted text-foreground font-medium flex-shrink-0">
                          {index + 1}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-foreground text-sm leading-tight">
                            {doc.name}
                            {doc.isRequired && (
                              <span className="text-destructive ml-1">*</span>
                            )}
                          </p>
                          <p className="text-xs text-red-400">
                            {`Accepted format: ${
                              doc.acceptedFormatsList?.join(", ") || "N/A"
                            } (Max ${formatFileSizeFromKB(doc.maxFileSizeKB)})`}
                          </p>
                        </div>
                      </div>

                      {/* Upload Button */}
                      <div className="space-y-2">
                        <input
                          type="file"
                          id={`file-mobile-${doc.id}`}
                          accept=".pdf,.jpeg,.jpg,.gif"
                          onChange={(e) =>
                            handleFileUpload(
                              doc.id,
                              e.target.files?.[0] || null,
                            )
                          }
                          className="hidden"
                        />
                        <Button
                          type="button"
                          onClick={() =>
                            document
                              .getElementById(`file-mobile-${doc.id}`)
                              ?.click()
                          }
                          variant={doc.file ? "outline" : "default"}
                          className={`w-full ${doc.file ? "border-primary text-primary hover:bg-primary/10" : "bg-primary hover:bg-primary/90 text-primary-foreground"}`}
                          size="sm"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {doc.file ? "Change File" : "Choose File"}
                        </Button>

                        {/* File Name Display */}
                        {doc.file && (
                          <div className="flex items-center gap-2 text-xs text-green-600 bg-green-50 p-2 rounded-lg">
                            <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
                            <span className="truncate">{doc.file.name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Submit Button */}
            <div className="flex items-center justify-end gap-4 pt-4 pb-8">
              <Button
                type="button"
                variant="outline"
                // onClick={() => {
                //   setStep('its');
                //   setOtp('');
                //   setOtpTimer(0);
                //   setViewOtp(false);
                //   setItsNumber("");
                //   setItsData(null);
                //   setMobileNumber("")
                //   setEmail("")
                //   setError("")
                //   if (timerIntervalRef.current) {
                //     clearInterval(timerIntervalRef.current);
                //   }
                // }}
                className="px-8 border-2 rounded-xl"
                style={{ borderColor: "#5B9BD5", color: "#5B9BD5" }}
                onClick={resetForm}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Change ITS Number
              </Button>
              <Button
                type="button"
                onClick={handleSubmit}
                disabled={isLoading}
                className="text-white shadow-lg hover:opacity-90 transition-opacity px-12 rounded-xl"
                style={{ backgroundColor: "#5B9BD5" }}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Saving Changes...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </div>
          </div>

          {/* Partner Dialog */}
          <Dialog
            open={isPartnerDialogOpen}
            onOpenChange={setIsPartnerDialogOpen}
          >
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
              <DialogHeader>
                <DialogTitle className="text-xl flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#5B9BD5]" />
                  {editingPartner ? "Edit Member" : "Add Member"}
                </DialogTitle>
                <DialogDescription>
                  {partnerFormData.isOfficeStaff
                    ? editingPartner
                      ? "Update office staff information"
                      : "Enter office staff details"
                    : editingPartner
                      ? "Update member information and documents"
                      : "Enter member details and upload required documents"}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6 py-4">
                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 tracking-wide">
                    Basic Information
                  </h3>

                  {/* ITS Fetch Section - Auto-fetch on 8th digit */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <div className="relative">
                        <InputFieldset
                          id="partnerITS"
                          label="ITS Number"
                          required
                          value={partnerFormData.itsNumber}
                          readOnly={partnerFormData.isNonItsPartner}
                          onChange={(e) => {
                            const value = e.target.value.replace(/\D/g, "");
                            setPartnerFormData((prev) => ({
                              ...prev,
                              itsNumber: value,
                            }));
                            setPartnerITSError("");
                            // Auto-fetch when 8 digits are entered
                            if (value.length === 8) {
                              handleFetchPartnerITS(value); // ✅ pass value
                            }
                          }}
                          placeholder="Enter 8-digit ITS number"
                          maxLength={8}
                          className={`${
                            partnerFormData.isNonItsPartner
                              ? "cursor-not-allowed bg-gray-100"
                              : ""
                          }`}
                          disabled={isFetchingPartnerITS}
                        />
                        {isFetchingPartnerITS && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <Loader2 className="w-4 h-4 animate-spin text-[#5B9BD5]" />
                          </div>
                        )}
                      </div>
                      {partnerITSError && (
                        <p className="text-xs text-red-600">
                          {partnerITSError}
                        </p>
                      )}
                      {isFetchingPartnerITS && (
                        <p className="text-xs text-[#5B9BD5]">
                          Fetching ITS details...
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <fieldset className="w-full rounded-[0.6rem] border-0 bg-transparent px-3 pb-2 pt-1 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.1)] transition-all focus-within:shadow-[inset_0_0_0_1px_rgba(59,130,246,0.6),0_0_0_3px_rgba(59,130,246,0.25)]">
                        <legend className="mb-1 bg-white px-1">
                          <span className="text-sm font-medium text-gray-700">
                            User Type
                          </span>
                        </legend>
                        <div className="flex h-6 items-center gap-3">
                          <Checkbox
                            id="partner-non-its"
                            checked={partnerFormData.isNonItsPartner}
                            onCheckedChange={(checked) =>
                              setPartnerFormData((prev) => ({
                                ...prev,
                                isNonItsPartner: !!checked,
                              }))
                            }
                          />
                          <label
                            htmlFor="partner-non-its"
                            className="cursor-pointer text-sm text-gray-700"
                          >
                            Non ITS User
                          </label>
                        </div>
                      </fieldset>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <InputFieldset
                        id="partnerName"
                        label="Full Name"
                        required
                        readOnly={!partnerFormData.isNonItsPartner}
                        value={partnerFormData.name}
                        onChange={(e) => {
                          const value = removeFullSpecialCharacters(
                            e.target.value,
                          );
                          setPartnerFormData({
                            ...partnerFormData,
                            name: value,
                          });
                          setPartnerFieldErrors((errors) => ({
                            ...errors,
                            name: "",
                          }));
                        }}
                        placeholder="Enter full name"
                        className={`${
                          !partnerFormData.isNonItsPartner
                            ? "cursor-not-allowed bg-gray-100"
                            : ""
                        }`}
                      />
                      {partnerFieldErrors.name && (
                        <p className="text-xs text-red-600">
                          {partnerFieldErrors.name}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <InputFieldset
                        id="partnerEmail"
                        label="Email"
                        required
                        type="email"
                        // value={partnerFormData.email}
                        readOnly={!partnerFormData.isNonItsPartner}
                        value={
                          partnerFormData.isNonItsPartner
                            ? partnerFormData.email
                            : maskEmail(partnerFormData.email)
                        }
                        onChange={(e) => {
                          setPartnerFormData({
                            ...partnerFormData,
                            email: e.target.value,
                          });
                          setPartnerEmailError("");
                        }}
                        placeholder="email@example.com"
                        className={`${
                          !partnerFormData.isNonItsPartner
                            ? "cursor-not-allowed bg-gray-100"
                            : ""
                        } ${partnerEmailError ? "border-red-500" : ""}`}
                      />
                      {partnerEmailError && (
                        <p className="text-xs text-red-600">
                          {partnerEmailError}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <InputFieldset
                        id="partnerMobile"
                        label="Mobile Number"
                        required
                        // value={partnerFormData.mobile}
                        readOnly={!partnerFormData.isNonItsPartner}
                        value={
                          partnerFormData.isNonItsPartner
                            ? partnerFormData.mobile
                            : maskMobileNumber(partnerFormData.mobile)
                        }
                        onChange={(e) => {
                          const value = validatePhoneNumberInput(
                            e.target.value,
                          );
                          setPartnerFormData({
                            ...partnerFormData,
                            mobile: value,
                          });
                          setPartnerFieldErrors((errors) => ({
                            ...errors,
                            mobile: "",
                          }));
                        }}
                        placeholder="Include country code eg. +91XXXXXXXX01"
                        className={`${
                          !partnerFormData.isNonItsPartner
                            ? "cursor-not-allowed bg-gray-100"
                            : ""
                        }`}
                      />
                      {partnerFieldErrors.mobile && (
                        <p className="text-xs text-red-600">
                          {partnerFieldErrors.mobile}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Configurations */}
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 tracking-wide">
                    Configurations
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    {/* Member Type - Radio Group */}
                    <RadioGroup
                      value={
                        partnerFormData.isPartner
                          ? "partner"
                          : partnerFormData.isOfficeStaff
                            ? "staff"
                            : ""
                      }
                      onValueChange={(value) => {
                        setPartnerFormData({
                          ...partnerFormData,
                          isPartner: value === "partner",
                          isOfficeStaff: value === "staff",
                        });
                        setPartnerFieldErrors((errors) => ({
                          ...errors,
                          memberType: "",
                        }));
                      }}
                      className={`col-span-2 grid gap-4 ${businessType === "sole_proprietor" ? "grid-cols-1" : "grid-cols-2"}`}
                    >
                      {businessType !== "sole_proprietor" && (
                        <div className="flex items-center space-x-3 p-3 border-2 rounded-lg bg-blue-50 border-blue-200 hover:border-blue-300 transition-colors">
                          <RadioGroupItem
                            value="partner"
                            id="isPartner"
                            className="size-5"
                          />
                          <Label
                            htmlFor="isPartner"
                            className="text-sm cursor-pointer font-medium text-gray-900"
                          >
                            Is Partner
                          </Label>
                        </div>
                      )}
                      <div className="flex items-center space-x-3 p-3 border-2 rounded-lg bg-purple-50 border-purple-200 hover:border-purple-300 transition-colors">
                        <RadioGroupItem
                          value="staff"
                          id="isOfficeStaff"
                          className="size-5"
                        />
                        <Label
                          htmlFor="isOfficeStaff"
                          className="text-sm cursor-pointer font-medium text-gray-900"
                        >
                          Is Office Staff
                        </Label>
                      </div>
                    </RadioGroup>
                    {partnerFieldErrors.memberType && (
                      <p className="col-span-2 text-xs text-red-600">
                        {partnerFieldErrors.memberType}
                      </p>
                    )}

                    <div className="flex items-center space-x-3 p-3 border rounded-lg bg-orange-50 border-orange-200">
                      <Checkbox
                        id="loginRequired"
                        checked={partnerFormData.loginRequired}
                        onCheckedChange={(checked) =>
                          setPartnerFormData({
                            ...partnerFormData,
                            loginRequired: !!checked,
                          })
                        }
                      />
                      <Label
                        htmlFor="loginRequired"
                        className="text-sm cursor-pointer font-medium"
                      >
                        Login Required
                      </Label>
                    </div>
                    <div className="flex items-center space-x-3 p-3 border rounded-lg bg-green-50 border-green-200">
                      <Checkbox
                        id="isAGL"
                        checked={partnerFormData.isAuthorizedGroupLeader}
                        onCheckedChange={(checked) =>
                          setPartnerFormData({
                            ...partnerFormData,
                            isAuthorizedGroupLeader: !!checked,
                          })
                        }
                      />
                      <Label
                        htmlFor="isAGL"
                        className="text-sm cursor-pointer font-medium"
                      >
                        Authorized Group Leader
                      </Label>
                    </div>
                  </div>
                </div>

                {/* Document Uploads */}
                {partnerFormData.isPartner && (
                  <div className="space-y-4">
                    <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      Document Uploads
                    </h3>
                    {partnerDocumentsError && (
                      <p className="text-xs text-red-600">
                        {partnerDocumentsError}
                      </p>
                    )}
                    <div className="space-y-3">
                      {partnerFormData.documents.map((doc) => (
                        <div
                          key={doc.id}
                          className="flex items-center justify-between p-3 border rounded-lg bg-gray-50"
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-700">
                              {doc.name}
                              {doc.isRequired && (
                                <span className="text-red-500 ml-1">*</span>
                              )}
                            </p>
                            {doc.file && (
                              <p className="text-xs text-gray-500 mt-1">
                                <span className="text-green-600">✓</span>{" "}
                                {doc.file.name}
                              </p>
                            )}
                            <p className="text-xs text-red-400">
                              {`Accepted format: ${
                                doc.acceptedFormatsList?.join(", ") || "N/A"
                              } (Max ${formatFileSizeFromKB(doc.maxFileSizeKB)})`}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="cursor-pointer">
                              <input
                                type="file"
                                className="hidden"
                                accept={
                                  doc.acceptedFormatsList?.length
                                    ? doc.acceptedFormatsList
                                        .map((f: string) => "." + f.trim())
                                        .join(",")
                                    : "*"
                                }
                                onChange={(e) => {
                                  const file = e.target.files?.[0] || null;
                                  if (!file) return;
                                  const isValid = validateFile(doc, file);
                                  if (!isValid) {
                                    e.target.value = ""; // reset input
                                    return;
                                  }
                                  handlePartnerFileUpload(doc.id, file);
                                }}
                              />
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-9"
                                asChild
                              >
                                <span>
                                  <Upload className="w-4 h-4 mr-2" />
                                  {doc.file ? "Change" : "Upload"}
                                </span>
                              </Button>
                            </label>
                            {doc.file && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() =>
                                  handlePartnerFileUpload(doc.id, null)
                                }
                                className="h-9 w-9 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setIsPartnerDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSavePartner}
                  className="text-white"
                  style={{ backgroundColor: "#5B9BD5" }}
                >
                  {editingPartner ? "Update Member" : "Add Member"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};
